/**
 * Task #5995 — normalize a thrown/rejected value into a REAL `Error` instance.
 *
 * When a non-`Error` value (most notably a plain `{ message, name, stack }`
 * object — the shape produced when an Error is structured-cloned across a
 * worker / native bridge, serialized, or hand-rolled) escapes as an unhandled
 * promise rejection, Sentry's `eventFromUnknownInput` cannot read a stack off it
 * and records only the object's KEY NAMES ("captured as promise rejection with
 * keys: message, name, stack"), discarding the actual message and stack. The
 * issue is then un-triageable.
 *
 * Wrapping such values into a real `Error` — preserving the original `message`,
 * `name`, and `stack` — before they escape means Sentry captures the readable
 * message/stack instead of the opaque keys.
 *
 * IMPORTANT: this is IDENTITY-PRESERVING for real `Error` instances — a value
 * that already `instanceof Error` is returned unchanged, so downstream
 * `instanceof BookingApiError` / sentinel-class checks and the "inline toast +
 * re-throw" behaviour keep working. Only genuine non-Errors are rebuilt.
 */
export function toRealError(value: unknown): Error {
  if (value instanceof Error) return value;
  // Plain object carrying error-like fields (e.g. a cloned/serialized Error).
  if (value && typeof value === "object") {
    const o = value as { message?: unknown; name?: unknown; stack?: unknown };
    const message =
      typeof o.message === "string" && o.message ? o.message : safeStringify(value);
    const err = new Error(message);
    if (typeof o.name === "string" && o.name) err.name = o.name;
    // Preserve the ORIGINAL stack when present so Sentry de-minifies to the real
    // frames instead of the (useless) frame where this wrapper ran.
    if (typeof o.stack === "string" && o.stack) err.stack = o.stack;
    return err;
  }
  // Primitive (string / number / boolean / null / undefined).
  return new Error(typeof value === "string" ? value : String(value));
}

/** Best-effort stringify that never throws (circular refs → `String(value)`). */
function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value) || String(value);
  } catch {
    return String(value);
  }
}
